import { z } from 'zod';
import { isoDateTime, uuid } from './common.js';
// --- customers (CUS-01 to CUS-11, docs/design/10 P2.2) ---------------------------------------------
export const CUSTOMER_STATUSES = ['active', 'pending', 'invited', 'disabled'];
export const customerStatus = z.enum(CUSTOMER_STATUSES);
export const CUSTOMER_STATUS_LABELS = { active: 'Aktiv', pending: 'Afventer godkendelse', invited: 'Inviteret', disabled: 'Spærret' };
const opt = (max) => z.string().trim().max(max).nullable().optional();
const email = z.string().trim().toLowerCase().email().max(200);
export const customerListItemDto = z.object({
    id: uuid,
    shopId: uuid,
    shopName: z.string(),
    email: z.string().nullable(),
    giftCode: z.string().nullable(),
    name: z.string(),
    company: z.string().nullable(),
    department: z.string().nullable(),
    status: customerStatus,
    hasPassword: z.boolean(),
    orderCount: z.number().int(),
    groupNames: z.array(z.string()),
    lastLoginAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const customerDto = z.object({
    id: uuid,
    shopId: uuid,
    shopName: z.string(),
    email: z.string().nullable(),
    giftCode: z.string().nullable(),
    status: customerStatus,
    hasPassword: z.boolean(),
    passwordAlgo: z.string().nullable(),
    name: z.string(),
    company: z.string().nullable(),
    phone: z.string().nullable(),
    street1: z.string().nullable(),
    street2: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    countryCode: z.string(),
    ean: z.string().nullable(),
    vatNumber: z.string().nullable(),
    vatValid: z.boolean().nullable(),
    vatCheckedAt: isoDateTime.nullable(),
    vatOverride: z.boolean(),
    department: z.string().nullable(),
    budget: z.string().nullable(),
    customFields: z.record(z.string(), z.string()),
    consentMarketing: z.boolean(),
    notes: z.string().nullable(),
    paymentTermsDays: z.number().int().nullable(),
    creditLimitMinor: z.number().int().nullable(),
    financeCustomerNumber: z.string().nullable(),
    demo: z.boolean(),
    language: z.string().nullable(),
    groupIds: z.array(uuid),
    externalId: z.string().nullable(),
    invitedAt: isoDateTime.nullable(),
    approvedAt: isoDateTime.nullable(),
    lastLoginAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
    orders: z.array(z.object({ id: uuid, number: z.number().int(), status: z.string(), totalMinor: z.number().int(), placedAt: isoDateTime.nullable() })),
});
export const saveCustomerRequest = z
    .object({
    shopId: uuid,
    email: email.nullable().optional(),
    giftCode: z.string().trim().max(60).nullable().optional(),
    /** Set a password directly (demo logins, phone support); otherwise invite. */
    password: z.string().min(8).max(200).nullable().optional(),
    status: customerStatus.default('active'),
    name: z.string().trim().max(200).default(''),
    company: opt(200),
    phone: opt(50),
    street1: opt(200),
    street2: opt(200),
    zip: opt(20),
    city: opt(100),
    countryCode: z.string().trim().length(2).default('DK'),
    ean: z.string().trim().regex(/^\d{13}$/, 'EAN er 13 cifre').nullable().optional().or(z.literal('').transform(() => null)),
    vatNumber: opt(20),
    vatOverride: z.boolean().default(false),
    vatValid: z.boolean().nullable().optional(),
    department: opt(100),
    budget: opt(100),
    customFields: z.record(z.string(), z.string().max(500)).default({}),
    consentMarketing: z.boolean().default(false),
    notes: opt(2000),
    paymentTermsDays: z.number().int().min(0).max(120).nullable().optional(),
    creditLimitMinor: z.number().int().min(0).nullable().optional(),
    financeCustomerNumber: opt(40),
    demo: z.boolean().default(false),
    language: z.enum(['da', 'en']).nullable().optional(),
    groupIds: z.array(uuid).max(50).default([]),
})
    .refine((v) => !!v.email || !!v.giftCode, { message: 'E-mail eller gavekode skal udfyldes', path: ['email'] });
export const customerQuery = z.object({
    shopId: uuid.optional(),
    q: z.string().trim().max(100).optional(),
    status: customerStatus.optional(),
    groupId: uuid.optional(),
    noOrders: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(500).default(50),
});
export const customerBulkRequest = z.object({
    ids: z.array(uuid).min(1).max(5000),
    action: z.enum(['approve', 'disable', 'enable', 'invite', 'delete']),
});
export const customerGroupDto = z.object({ id: uuid, shopId: uuid, name: z.string(), memberCount: z.number().int(), categoryIds: z.array(uuid) });
export const saveCustomerGroupRequest = z.object({ name: z.string().trim().min(1).max(100), categoryIds: z.array(uuid).max(500).default([]) });
export const viesResultDto = z.object({ vatNumber: z.string(), valid: z.boolean(), name: z.string().nullable(), address: z.string().nullable(), checkedAt: isoDateTime, cached: z.boolean() });
// --- import (CUS-04) -------------------------------------------------------------------------------
export const CUSTOMER_IMPORT_FIELDS = ['email', 'giftCode', 'password', 'name', 'company', 'phone', 'street1', 'street2', 'zip', 'city', 'countryCode', 'ean', 'vatNumber', 'department', 'budget', 'group', 'externalId', 'custom'];
export const CUSTOMER_IMPORT_LABELS = {
    email: 'E-mail',
    giftCode: 'Brugernavn / gavekode',
    password: 'Adgangskode',
    name: 'Navn',
    company: 'Firma',
    phone: 'Telefon',
    street1: 'Adresse',
    street2: 'Adresse 2',
    zip: 'Postnr.',
    city: 'By',
    countryCode: 'Land',
    ean: 'EAN',
    vatNumber: 'CVR / moms',
    department: 'Afdeling',
    budget: 'Budget',
    group: 'Gruppe',
    externalId: 'Eksternt id',
    custom: 'Eget felt (kolonnens navn)',
};
export const customerImportDto = z.object({
    id: uuid,
    shopId: uuid,
    fileName: z.string(),
    status: z.enum(['uploaded', 'queued', 'running', 'done', 'failed']),
    headers: z.array(z.string()),
    sample: z.array(z.array(z.string())),
    mapping: z.record(z.string(), z.string()),
    invite: z.boolean(),
    groupId: uuid.nullable(),
    rows: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(z.object({ row: z.number().int(), message: z.string() })),
    createdAt: isoDateTime,
    finishedAt: isoDateTime.nullable(),
});
export const startCustomerImportRequest = z.object({
    mapping: z.record(z.string().regex(/^\d+$/), z.enum(CUSTOMER_IMPORT_FIELDS)),
    invite: z.boolean().default(false),
    groupId: uuid.nullable().optional(),
});
// --- storefront authentication ---------------------------------------------------------------------
/**
 * `login` is whatever the customer was given: an e-mail, an employee number, a gift code or any
 * other string. It matches the e-mail or the username (gift_code) of a customer in the shop.
 */
export const storefrontLoginRequest = z
    .object({ login: z.string().trim().min(1).max(200).optional(), email: email.optional(), giftCode: z.string().trim().min(1).max(60).optional(), password: z.string().min(1).max(200).optional() })
    .refine((v) => !!v.login || !!v.email || !!v.giftCode, { message: 'E-mail eller brugernavn mangler' });
export const storefrontRegisterRequest = z.object({
    email,
    password: z.string().min(10).max(200),
    name: z.string().trim().min(1).max(200),
    company: opt(200),
    phone: opt(50),
    vatNumber: opt(20),
    department: opt(100),
    consentMarketing: z.boolean().default(false),
    /** Proof of work from the storefront (ALTCHA) and a honeypot that must stay empty. */
    altcha: z.string().min(10).max(2000),
    website: z.string().max(0).optional(),
});
export const storefrontResetRequest = z.object({ email, altcha: z.string().min(10).max(2000) });
export const storefrontSetPasswordRequest = z.object({ token: z.string().min(20).max(200), password: z.string().min(10).max(200) });
export const storefrontTokenRequest = z.object({ token: z.string().min(20).max(200) });
export const storefrontCustomerDto = z.object({
    id: uuid,
    shopId: uuid,
    email: z.string().nullable(),
    giftCode: z.string().nullable(),
    name: z.string(),
    company: z.string().nullable(),
    phone: z.string().nullable(),
    street1: z.string().nullable(),
    street2: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    countryCode: z.string(),
    department: z.string().nullable(),
    budget: z.string().nullable(),
    ean: z.string().nullable(),
    vatNumber: z.string().nullable(),
    language: z.string().nullable(),
    groupIds: z.array(uuid),
    demo: z.boolean(),
});
//# sourceMappingURL=customers.js.map